import { BrowserModule } from '@angular/platform-browser';
import { NgModule, LOCALE_ID, ErrorHandler, inject, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';

import {
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel,
  IPublicClientApplication,
  InteractionType,
} from '@azure/msal-browser';

import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { TraceService, createErrorHandler } from '@sentry/angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PrivateLayoutComponent } from 'src/app/core/layouts/private.layout';
import { PublicLayoutComponent } from 'src/app/core/layouts/public.layout';
import { HeaderComponent } from './core/header/header.component';
import { SharedModule } from './shared/shared.module';
import { PrivacyComponent } from './core/privacy/privacy.component';
import { HelpComponent } from './core/help/help.component';
import { AccessDeniedComponent } from './core/access-denied/access-denied.component';
import { MaintenanceModeComponent } from './core/maintenance-mode/maintenance-mode.component';
import { environment } from 'src/environments/environment';
import { AuthInterceptor } from 'src/api-authorization/auth.interceptor';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';

export function MSALInstanceFactory(): PublicClientApplication {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.azure.clientId,
      authority: environment.azure.authority,
      redirectUri: environment.azure.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message) => {
          console.log(`MSAL Log: [${LogLevel[logLevel]}] ${message}`);
        },
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false
      }
    }
  });

  return msalInstance;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const interactionType: MsalGuardConfiguration['interactionType'] = InteractionType.Redirect;
  return {
    interactionType,
    authRequest: {
      scopes: [environment.azure.scope]
    }
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const interactionType: MsalInterceptorConfiguration['interactionType'] = InteractionType.Redirect;
  return {
    interactionType,
    protectedResourceMap: new Map([[environment.url, [environment.azure.scope]]])
  };
}

export function initializeMsalFactory(pca: IPublicClientApplication) {
  return () => pca.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    PublicLayoutComponent,
    PrivateLayoutComponent,
    HeaderComponent,
    PrivacyComponent,
    HelpComponent,
    AccessDeniedComponent,
    MaintenanceModeComponent,
    LoginComponent,
    LogoutComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    AppRoutingModule,
    SharedModule,
    MsalModule.forRoot(
      MSALInstanceFactory(),
      MSALGuardConfigFactory(),
      MSALInterceptorConfigFactory()
    )
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'en-AU' },

    // Initialize MSAL before anything else (guards, interceptors, etc.) can touch it
    { provide: APP_INITIALIZER, useFactory: initializeMsalFactory, deps: [MSAL_INSTANCE], multi: true },

    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    MsalService,
    MsalGuard,
    MsalBroadcastService,

    {
      provide: ErrorHandler,
      useValue: createErrorHandler({ showDialog: true }),
    },

    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => { inject(TraceService); }
    },

    provideHttpClient(withInterceptorsFromDi()),

    providePrimeNG({
      theme: {
        preset: Aura,
        options: { cssLayer: { name: 'primeng', order: 'app, primeng' } }
      }
    })
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
